<template>
  <div class="flex h-screen overflow-hidden bg-gray-50">
    <PWAbanner />
    <!-- Sidebar -->
    <aside
      v-if="showLayout"
      class="z-[999] flex-shrink-0 h-full overflow-y-auto bg-white border-r border-gray-200"
    >
      <SchoolSidebar />
    </aside>

    <!-- Main Content -->
    <div class="flex-1 flex flex-col overflow-hidden">
      <!-- Top Bar -->
      <header
        v-if="showLayout"
        class="bg-white shadow-sm border-b border-gray-200 px-6 flex-shrink-0"
      >
        <SchoolHeader />
      </header>

      <!-- Main Content Area -->
      <main class="flex-1 overflow-y-auto px-6 py-4">
        <router-view />
      </main>
    </div>

    <!-- Global Toast -->
    <GlobalToast
      :message="toastState"
      @hide="hideToast"
    />
  </div>
</template>

<script setup>
  import { computed } from "vue";
  import { useRoute } from "vue-router";
  import { useStore } from "vuex";
  import PWAbanner from "@/components/PWAInstallBanner/PWAInstallBanner.vue";
  import SchoolHeader from "./components/Header/SchoolHeader.vue";
  import SchoolSidebar from "./components/Sidebar/SchoolSidebar.vue";
  import GlobalToast from "@/utils/GlobalToast.vue";
  import { useGlobalToast } from "@/utils/useGlobalToast";

  const store = useStore();
  const route = useRoute();

  // Auth route check
  const isAuthRoute = computed(() => {
    const path = route.path;
    return (
      path === "/login" ||
      path === "/signup" ||
      path === "/scanedTeacherAttendence" ||
      path === "/createSchool" ||
      path === "/404" ||
      path === "/forgot-password" ||
      path === "/onboarding" ||
      path === "/AttendanceMethods" ||
      /^\/mark-attendance\/[^/]+$/.test(path)
    );
  });

  // Total classes computation
  const TotalClasses = computed(() => {
    const classes = store.getters.allClasses || [];
    return classes.filter((c) => (c?.name || "").toLowerCase() !== "passout")
      .length;
  });

  // Determine if header/sidebar should be shown
  const showLayout = computed(() => {
    // Hide layout for auth routes
    if (isAuthRoute.value) return false;

    // Hide layout if no classes
    if (route.path === "/classes" && TotalClasses.value === 0) return false;

    return true;
  });

  // Toast state
  const { toastState, hideToast } = useGlobalToast();
</script>

<style>
  html,
  body,
  #app {
    height: 100%;
    margin: 0;
    padding: 0;
    font-size: 14.5px;
    background-color: #f9fafb;
    font-family: "Inter", Avenir, Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  /* Smooth scrolling for both */
  main,
  aside {
    scroll-behavior: smooth;
  }

  /* Scrollbar width */
  main::-webkit-scrollbar,
  aside::-webkit-scrollbar {
    width: 8px;
  }

  /* Scrollbar thumb */
  main::-webkit-scrollbar-thumb,
  aside::-webkit-scrollbar-thumb {
    background: #cbd5e1; /* Tailwind gray-300 */
    border-radius: 10px;
    transition: background 0.3s ease;
  }

  /* Hover effect */
  main::-webkit-scrollbar-thumb:hover,
  aside::-webkit-scrollbar-thumb:hover {
    background: #94a3b8; /* Tailwind gray-400 */
  }

  /* Optional scrollbar track background */
  main::-webkit-scrollbar-track,
  aside::-webkit-scrollbar-track {
    background: #f1f5f9; /* Tailwind gray-100 */
  }

  /* ==========================
     ✅ Fine-Tuned Breakpoints
     ========================== */

  /* 📺 Large monitors (wider than 1920px) → Slightly larger text */
  @media (min-width: 1921px) {
    html {
      font-size: 18px;
    }
  }

  /* 💻 Laptops (Acer Aspire 7, 1366px–1920px) → Default */
  /* @media (min-width: 1025px) and (max-width: 1920px) {
    html {
      font-size: 11px;
    }
  } */
</style>
